import clsx from 'clsx'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Spinner, { LoadingSpinner } from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import Widget from '@/components-latest/primitives/Widget'
import BestSellingModal from '@/components-latest/ui/PerformancePage/BestSellingModal'
import ProductStatsCard from '@/components-latest/ui/PerformancePage/ProductStatsCard'
import Store from '@/components-latest/primitives/Icons/Store'
import InfoCircle from '@/components-latest/primitives/Icons/InfoCircle'
import { BestSellingResponse } from '@/ducks/performance/performance.types'

import Styles from './BestSellingPerformance.module.scss'

interface BranchBestSellingData {
  label: string
  data: BestSellingResponse
}

interface IBestSellingPerformanceProps {
  branches: BranchBestSellingData[]
  isLoading?: boolean
  error?: string | null
}

const BestSellingPerformance: React.FC<IBestSellingPerformanceProps> = ({
  branches,
  isLoading = false,
  error = null
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<{ open: boolean; items: any[]; title: string }>({ open: false, items: [], title: '' })

  if (isLoading) {
    return (
      <ContentBlock className={clsx(Styles.spinnerWrapper)}>
        <LoadingSpinner 
          containerType="centered" 
          text={t('performance.loading.bestSelling', 'Loading best selling data...')}
        />
      </ContentBlock>
    )
  }
  if (error) {
    return (
      <ContentBlock className={clsx(Styles.spinnerWrapper)}>
        <Text color="danger">{error}</Text>
      </ContentBlock>
    )
  }

  return (
    <ContentBlock className={Styles.contentBlock} data-testid="best-selling-performance">
      <div className={Styles.bestSellingList}>
        {branches.length === 0 ? (
          <Text>{t('performance.not-enough-data')}</Text>
        ) : (
          branches.map((branch, idx) => {
            const hasData = branch.data?.bestSellingPerformance?.length > 0
            return (
              <div key={idx} className={Styles.branchGroup}>
                {branch.label && (
                  <div className={clsx(Styles.branchLabelRow, { [Styles.withButton]: hasData })}>
                    <div className={Styles.icon}><Store /></div>
                    <Text variant="subtitle" className={Styles.branchLabelText}>{branch.label}</Text>
                    {hasData && (
                      <Widget
                        className={Styles.showAllButton}
                        onClick={() => setOpen({ open: true, items: branch.data.bestSellingPerformance, title: branch.label })}
                        disabled={!hasData}
                      >
                        {t('performance.reviews.view-all')}
                      </Widget>
                    )}
                  </div>
                )}
                <div className={Styles.branchItemsRow}>
                  {hasData ? (
                    branch.data.bestSellingPerformance.slice(0, 3).map(item => (
                      <ProductStatsCard key={item.item_id} {...item} />
                    ))
                  ) : (
                    <div className={Styles.emptyCard}>
                      <span className={Styles.emptyText}>{t('performance.not-enough-data')}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
        {open.open && (
          <BestSellingModal
            bestSellingItems={open.items}
            open={open.open}
            onOpenChange={isOpen => setOpen(state => ({ ...state, open: isOpen }))}
            title={open.title}
          />
        )}
      </div>
    </ContentBlock>
  )
}

export default BestSellingPerformance
